/**
 * 支付通道策略（组 header + 成员 detail）契约 schema —— dev-docs `02-API契约/A8-运营支付.md`
 * §7.8.1~§7.8.4（ADM-PAY-S008 通道启停 + 排序 + Region 覆盖）。
 *
 * 三态可区分（§7.8.0）是本组 schema 的设计前提：
 *   - `payment_methods: []`（PUT）= 空策略：组保留、成员清空，serving 返空态且**不回退全局**；
 *   - DELETE = 删组：该 Region 恢复继承全局策略；
 *   - 全局组（`region_code: null`）**不可删** —— 故 DELETE 的 query schema 里 `region_code` 非空必填。
 */

import { z } from 'zod';
import { type PaymentMethod, PaymentMethodSchema } from './payment-methods';

/**
 * 商店地区码：ISO 3166-1 alpha-2 大写 2 字符。
 * 与 `system_regions.region_code`、C 端归一后的 `X-Store-Region` 同编码（A8 §7.8.1 字段表）。
 * 值域（是否属 `system_regions` 完整预置清单）是跨表事实，由 service 层校验，Zod 只保证格式。
 */
export const PaymentMethodPolicyRegionCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'region_code must be an upper-case ISO 3166-1 alpha-2 code');

/**
 * 有序**支付方式**数组：元素取值域 = `03-数据字典/70-产品配置.md` §3 两层闭集的 `PAYMENT_METHODS` 层
 * 且不得重复。**成员是支付方式、不是结算通道**（A8 §7.8.2「双向拒收」）：同一结算通道下的多个支付
 * 方式各占一项、各自排序，传结算通道值 `STRIPE` → VALIDATION_FAILED（`IAP` 两层同名，属本层合法取值）。
 * **数组位置即 `sort_order`**（服务端按下标 0..n-1 写入），故本 schema 不接收 `sort_order` 入参。
 * `[]` 合法 = 空策略。
 */
export const PaymentMethodPolicyMembersSchema = z
  .array(PaymentMethodSchema)
  .refine((methods) => new Set(methods).size === methods.length, {
    message: 'payment_methods must not contain duplicate payment methods',
  });

/**
 * PUT /api/v1/payment-method-policies 请求体（§7.8.2）。
 * **strict**：请求体字段闭集 = 下面三键，任何额外字段 → VALIDATION_FAILED（typo 不静默 strip）。
 * `region_code` 必填但可为 `null`（= 写 App 全局策略组）—— 故用 `.nullable()` 而非 `.nullish()`：
 * 省略该键即校验失败。
 */
export const UpsertPaymentMethodPolicyRequestSchema = z
  .object({
    app_id: z.string().uuid(),
    region_code: PaymentMethodPolicyRegionCodeSchema.nullable(),
    payment_methods: PaymentMethodPolicyMembersSchema,
  })
  .strict();
export type UpsertPaymentMethodPolicyRequest = z.infer<
  typeof UpsertPaymentMethodPolicyRequestSchema
>;

/**
 * PUT /api/v1/payment-method-policies/:policy_id/order 请求体（§7.8.10 条件调序）。
 * **strict**：闭集两键。`expected_payment_methods` = 调用方读到的该组成员（只做集合核验、顺序不参与比较），
 * `payment_methods` = 调序后的完整有序数组，必须是预期集合的一个**排列**（元素相同、不增不减、不重复），
 * 否则 VALIDATION_FAILED —— 本端点只改顺序、不增删成员（增删走 §7.8.2）。
 */
export const ReorderPaymentMethodPolicyRequestSchema = z
  .object({
    expected_payment_methods: PaymentMethodPolicyMembersSchema,
    payment_methods: PaymentMethodPolicyMembersSchema,
  })
  .strict()
  .refine(
    (body) =>
      body.payment_methods.length === body.expected_payment_methods.length &&
      body.payment_methods.every((method) => body.expected_payment_methods.includes(method)),
    {
      path: ['payment_methods'],
      message: 'payment_methods must be a permutation of expected_payment_methods',
    },
  );
export type ReorderPaymentMethodPolicyRequest = z.infer<
  typeof ReorderPaymentMethodPolicyRequestSchema
>;

/**
 * DELETE /api/v1/payment-method-policies query（§7.8.4）。
 * `region_code` **必须非空**：全局策略组不可删（关闭该 App 全部通道走 §7.8.2 传 `payment_methods: []`），
 * 传 `null` / 省略 → VALIDATION_FAILED。
 */
export const DeletePaymentMethodPolicyQuerySchema = z.object({
  app_id: z.string().uuid(),
  region_code: PaymentMethodPolicyRegionCodeSchema,
});
export type DeletePaymentMethodPolicyQuery = z.infer<typeof DeletePaymentMethodPolicyQuerySchema>;

/**
 * 策略组成员（§7.8.1 `payment_methods[]`）：`payment_method` 为**支付方式**层取值，
 * `sort_order` 为该策略内排序配置值，同策略内互不相同。
 */
export type PaymentMethodPolicyItemResponse = {
  payment_method: PaymentMethod;
  sort_order: number;
};

/**
 * 单条策略（§7.8.1 item shape，§7.8.2 / §7.8.3 复用）。
 * `region_code: null` = App 全局策略组；`payment_methods: []` = 空策略（组存在、零成员）。
 */
export type PaymentMethodPolicyResponse = {
  policy_id: string;
  region_code: string | null;
  payment_methods: PaymentMethodPolicyItemResponse[];
};

/** DELETE 响应（§7.8.4）：被删 Region 覆盖组的标识三元组。 */
export type DeletePaymentMethodPolicyResponse = {
  policy_id: string;
  app_id: string;
  region_code: string;
};
